import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { usePortfolio } from '../../context/PortfolioContext';
import { getAnalyticsOverview, getAnalyticsAttribution, getAnalyticsDrawdown, getAnalyticsSectorHeatmap } from '../../services/api';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { TrendingUp, TrendingDown, Activity, BarChart3 } from 'lucide-react';
// @ts-ignore
import Spinner from '../../components/Spinner';

// ── Types ─────────────────────────────────────────────────────────────────────
interface RiskMetrics {
  sharpe_ratio: number | null;
  sortino_ratio: number | null;
  annualized_return: number | null;
  annualized_volatility: number | null;
  total_return: number | null;
}

interface DrawdownPoint {
  date: string;
  drawdown: number;
}

interface OverviewData {
  risk_metrics: RiskMetrics;
  drawdown: {
    max_drawdown_pct: number | null;
    current_drawdown: number | null;
    drawdown_series: DrawdownPoint[];
  };
  holdings_count: number;
  total_value: number;
}

interface TickerAttribution {
  ticker: string;
  weight: number;
  contribution: number;
  pnl: number;
}

interface SectorAttribution {
  sector: string;
  contribution: number;
  weight: number;
}

interface AttributionData {
  by_ticker: TickerAttribution[];
  by_sector: SectorAttribution[];
}

interface DrawdownData {
  max_drawdown_pct: number | null;
  drawdown_start: string | null;
  drawdown_end: string | null;
  current_drawdown: number | null;
  drawdown_series: DrawdownPoint[];
}

interface SectorHeatmapTicker {
  ticker: string;
  return_pct: number;
}

interface SectorHeatmapEntry {
  sector: string;
  avg_return: number;
  tickers: SectorHeatmapTicker[];
}

interface SectorHeatmapData {
  sectors: SectorHeatmapEntry[];
}

// ── Period options ─────────────────────────────────────────────────────────────
const PERIODS = ['1M', '3M', '6M', '1Y', 'ALL'] as const;
type Period = typeof PERIODS[number];

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtPct = (n: number | null | undefined): string => {
  if (n == null || isNaN(n)) return '--';
  return `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`;
};

const fmtRatio = (n: number | null | undefined): string => {
  if (n == null || isNaN(n)) return '--';
  return n.toFixed(2);
};

const colorForValue = (val: number | null | undefined): string => {
  if (val == null || isNaN(val)) return 'text-slate-700';
  return val >= 0 ? 'text-emerald-600' : 'text-red-500';
};

/**
 * Compute a background color for heatmap cells.
 * Negative returns -> red, zero -> white, positive -> green.
 * Clamps at +/-10% for full saturation.
 */
const heatmapBg = (avgReturn: number): string => {
  const clamped = Math.max(-10, Math.min(10, avgReturn));
  const t = clamped / 10; // -1..1
  if (t >= 0) {
    // white to green
    const r = Math.round(255 - t * 119); // 255 -> 136
    const g = Math.round(255 - t * 15);  // 255 -> 240
    const b = Math.round(255 - t * 137); // 255 -> 118
    return `rgb(${r}, ${g}, ${b})`;
  } else {
    // white to red
    const absT = Math.abs(t);
    const r = 255;
    const g = Math.round(255 - absT * 155); // 255 -> 100
    const b = Math.round(255 - absT * 155);
    return `rgb(${r}, ${g}, ${b})`;
  }
};

// ── KPI Card ──────────────────────────────────────────────────────────────────
interface KpiCardProps {
  label: string;
  value: string;
  icon: React.ReactNode;
  valueColor?: string;
}

const KpiCard: React.FC<KpiCardProps> = ({ label, value, icon, valueColor = 'text-slate-900' }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
    <div className="flex items-center gap-2 mb-2">
      <span className="text-slate-400">{icon}</span>
      <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">{label}</span>
    </div>
    <div className={`text-2xl font-bold font-mono ${valueColor}`}>{value}</div>
  </div>
);

// ── Custom Tooltip for Drawdown Chart ─────────────────────────────────────────
const DrawdownTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-lg px-3 py-2 text-xs">
      <div className="font-semibold text-slate-600 mb-1">{label}</div>
      <div className="text-red-600 font-mono font-bold">{payload[0].value.toFixed(2)}%</div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
const AnalyticsView: React.FC = () => {
  const { currentUser } = useAuth();
  const { activePortfolioId, activePortfolioSummary } = usePortfolio();

  const [period, setPeriod] = useState<Period>('1Y');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [overview, setOverview] = useState<OverviewData | null>(null);
  const [attribution, setAttribution] = useState<AttributionData | null>(null);
  const [drawdown, setDrawdown] = useState<DrawdownData | null>(null);
  const [sectorHeatmap, setSectorHeatmap] = useState<SectorHeatmapData | null>(null);

  const fetchAllData = useCallback(async (selectedPeriod: Period) => {
    if (!currentUser || !activePortfolioId) return;

    setIsLoading(true);
    setError(null);

    try {
      const token = await currentUser.getIdToken();

      const [overviewRes, attributionRes, drawdownRes, heatmapRes] = await Promise.all([
        getAnalyticsOverview(token, activePortfolioId, selectedPeriod),
        getAnalyticsAttribution(token, activePortfolioId, selectedPeriod),
        getAnalyticsDrawdown(token, activePortfolioId),
        getAnalyticsSectorHeatmap(token, activePortfolioId),
      ]);

      setOverview(overviewRes);
      setAttribution(attributionRes);
      setDrawdown(drawdownRes);
      setSectorHeatmap(heatmapRes);
    } catch (err: any) {
      console.error('Analytics fetch failed:', err);
      setError(err.message || 'Failed to load analytics data.');
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, activePortfolioId]);

  // Fetch data when portfolio or period changes
  useEffect(() => {
    if (activePortfolioId) {
      fetchAllData(period);
    } else {
      setOverview(null);
      setAttribution(null);
      setDrawdown(null);
      setSectorHeatmap(null);
    }
  }, [activePortfolioId, period, fetchAllData]);

  const handlePeriodChange = (newPeriod: Period) => {
    setPeriod(newPeriod);
  };

  // ── No portfolio selected ──
  if (!activePortfolioId) {
    return (
      <div className="h-full overflow-y-auto bg-slate-50">
        <div className="max-w-7xl mx-auto p-6">
          <div className="bg-white rounded-xl border border-dashed border-slate-300 p-16 text-center">
            <BarChart3 size={40} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-600 font-semibold text-lg">No Portfolio Selected</p>
            <p className="text-sm text-slate-400 mt-2">
              Select or create a portfolio to view analytics.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Loading state ──
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full bg-slate-50">
        <Spinner />
      </div>
    );
  }

  // ── Error state ──
  if (error) {
    return (
      <div className="h-full overflow-y-auto bg-slate-50">
        <div className="max-w-7xl mx-auto p-6">
          <div className="bg-white rounded-xl border border-red-200 p-10 text-center">
            <Activity size={36} className="mx-auto text-red-400 mb-3" />
            <p className="text-red-600 font-semibold">Failed to Load Analytics</p>
            <p className="text-sm text-slate-500 mt-2">{error}</p>
            <button
              onClick={() => fetchAllData(period)}
              className="mt-4 px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Data extraction ──
  const risk = overview?.risk_metrics;
  const drawdownSeries = drawdown?.drawdown_series || overview?.drawdown?.drawdown_series || [];
  const maxDrawdownPct = drawdown?.max_drawdown_pct ?? overview?.drawdown?.max_drawdown_pct ?? null;
  const tickerAttribution = attribution?.by_ticker || [];
  const sectorAttribution = attribution?.by_sector || [];
  const sectors = sectorHeatmap?.sectors || [];

  // Top 15 tickers sorted by absolute contribution
  const topTickers = [...tickerAttribution]
    .sort((a, b) => Math.abs(b.contribution) - Math.abs(a.contribution))
    .slice(0, 15);

  return (
    <div className="h-full overflow-y-auto bg-slate-50">
      <div className="max-w-7xl mx-auto p-6 space-y-6">

        {/* ── Header + Period Selector ── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <Activity size={22} className="text-blue-500" />
              Portfolio Analytics
            </h1>
            <p className="text-xs text-slate-400 mt-1">
              {overview ? `${overview.holdings_count} holdings` : ''}{' '}
              {activePortfolioSummary?.name ? `- ${activePortfolioSummary.name}` : ''}
            </p>
          </div>

          <div className="flex items-center gap-1 bg-white rounded-lg border border-slate-200 p-1 shadow-sm">
            {PERIODS.map((p) => (
              <button
                key={p}
                onClick={() => handlePeriodChange(p)}
                className={`px-4 py-1.5 text-xs font-bold rounded-md transition-colors ${
                  period === p
                    ? 'bg-blue-600 text-white'
                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                }`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>

        {/* ── KPI Cards ── */}
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          <KpiCard
            label="Sharpe Ratio"
            value={fmtRatio(risk?.sharpe_ratio)}
            icon={<TrendingUp size={16} />}
            valueColor={colorForValue(risk?.sharpe_ratio)}
          />
          <KpiCard
            label="Sortino Ratio"
            value={fmtRatio(risk?.sortino_ratio)}
            icon={<TrendingUp size={16} />}
            valueColor={colorForValue(risk?.sortino_ratio)}
          />
          <KpiCard
            label="Max Drawdown"
            value={maxDrawdownPct != null ? `${maxDrawdownPct.toFixed(2)}%` : '--'}
            icon={<TrendingDown size={16} />}
            valueColor="text-red-500"
          />
          <KpiCard
            label="Annualized Return"
            value={fmtPct(risk?.annualized_return)}
            icon={<BarChart3 size={16} />}
            valueColor={colorForValue(risk?.annualized_return)}
          />
          <KpiCard
            label="Annual Volatility"
            value={risk?.annualized_volatility != null ? `${risk.annualized_volatility.toFixed(2)}%` : '--'}
            icon={<Activity size={16} />}
            valueColor="text-amber-600"
          />
          <KpiCard
            label="Total Return"
            value={fmtPct(risk?.total_return)}
            icon={risk?.total_return != null && risk.total_return >= 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
            valueColor={colorForValue(risk?.total_return)}
          />
        </div>

        {/* ── Drawdown Chart ── */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <h2 className="text-lg font-bold text-slate-800 mb-4">Drawdown Over Time</h2>
          {drawdownSeries.length > 0 ? (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={drawdownSeries} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis
                    dataKey="date"
                    fontSize={11}
                    stroke="#94a3b8"
                    tickFormatter={(val: string) => {
                      if (!val) return '';
                      const parts = val.split('-');
                      return parts.length >= 2 ? `${parts[1]}/${parts[0]?.slice(2)}` : val;
                    }}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    fontSize={11}
                    stroke="#94a3b8"
                    tickFormatter={(val: number) => `${val.toFixed(0)}%`}
                    domain={['dataMin', 0]}
                  />
                  <Tooltip content={<DrawdownTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="drawdown"
                    stroke="#dc2626"
                    fill="#ef4444"
                    fillOpacity={0.3}
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-48 flex items-center justify-center text-slate-400 text-sm">
              No drawdown data available for this period.
            </div>
          )}
        </div>

        {/* ── Attribution Charts (Two Columns) ── */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {/* By Ticker */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <h2 className="text-lg font-bold text-slate-800 mb-4">Attribution by Ticker (Top 15)</h2>
            {topTickers.length > 0 ? (
              <div style={{ height: Math.max(280, topTickers.length * 28 + 40) }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={topTickers}
                    layout="vertical"
                    margin={{ top: 5, right: 30, left: 60, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                    <XAxis
                      type="number"
                      fontSize={11}
                      stroke="#94a3b8"
                      tickFormatter={(val: number) => `${val.toFixed(1)}%`}
                    />
                    <YAxis
                      type="category"
                      dataKey="ticker"
                      fontSize={11}
                      stroke="#64748b"
                      width={50}
                    />
                    <Tooltip
                      formatter={(val: number) => [`${val.toFixed(3)}%`, 'Contribution']}
                      contentStyle={{ borderRadius: '8px', fontSize: '12px' }}
                    />
                    <Bar dataKey="contribution" radius={[0, 4, 4, 0]} barSize={16}>
                      {topTickers.map((entry, index) => (
                        <Cell
                          key={`ticker-${index}`}
                          fill={entry.contribution >= 0 ? '#10b981' : '#ef4444'}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-48 flex items-center justify-center text-slate-400 text-sm">
                No ticker attribution data available.
              </div>
            )}
          </div>

          {/* By Sector */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <h2 className="text-lg font-bold text-slate-800 mb-4">Attribution by Sector</h2>
            {sectorAttribution.length > 0 ? (
              <div style={{ height: Math.max(280, sectorAttribution.length * 36 + 40) }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={sectorAttribution}
                    layout="vertical"
                    margin={{ top: 5, right: 30, left: 80, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                    <XAxis
                      type="number"
                      fontSize={11}
                      stroke="#94a3b8"
                      tickFormatter={(val: number) => `${val.toFixed(1)}%`}
                    />
                    <YAxis
                      type="category"
                      dataKey="sector"
                      fontSize={11}
                      stroke="#64748b"
                      width={70}
                    />
                    <Tooltip
                      formatter={(val: number) => [`${val.toFixed(3)}%`, 'Contribution']}
                      contentStyle={{ borderRadius: '8px', fontSize: '12px' }}
                    />
                    <Bar dataKey="contribution" radius={[0, 4, 4, 0]} barSize={20}>
                      {sectorAttribution.map((entry, index) => (
                        <Cell
                          key={`sector-${index}`}
                          fill={entry.contribution >= 0 ? '#10b981' : '#ef4444'}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-48 flex items-center justify-center text-slate-400 text-sm">
                No sector attribution data available.
              </div>
            )}
          </div>
        </div>

        {/* ── Sector Heatmap ── */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <h2 className="text-lg font-bold text-slate-800 mb-4">Sector Return Heatmap</h2>
          {sectors.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
              {sectors.map((sector) => (
                <div
                  key={sector.sector}
                  className="rounded-lg p-4 border border-slate-100 transition-shadow hover:shadow-md cursor-default"
                  style={{ backgroundColor: heatmapBg(sector.avg_return) }}
                >
                  <div className="text-xs font-bold text-slate-800 truncate mb-1">
                    {sector.sector}
                  </div>
                  <div
                    className={`text-lg font-bold font-mono ${
                      sector.avg_return >= 0 ? 'text-emerald-800' : 'text-red-800'
                    }`}
                  >
                    {fmtPct(sector.avg_return)}
                  </div>
                  {sector.tickers && sector.tickers.length > 0 && (
                    <div className="mt-2 space-y-0.5">
                      {sector.tickers.slice(0, 4).map((t) => (
                        <div key={t.ticker} className="flex items-center justify-between text-xs">
                          <span className="text-slate-700 truncate font-medium">{t.ticker}</span>
                          <span
                            className={`font-mono font-semibold ml-1 ${
                              t.return_pct >= 0 ? 'text-emerald-700' : 'text-red-700'
                            }`}
                          >
                            {fmtPct(t.return_pct)}
                          </span>
                        </div>
                      ))}
                      {sector.tickers.length > 4 && (
                        <div className="text-xs text-slate-500 pt-0.5">
                          +{sector.tickers.length - 4} more
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="h-32 flex items-center justify-center text-slate-400 text-sm">
              No sector heatmap data available.
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default AnalyticsView;
